import React, { useCallback, useRef, useState } from 'react'
import Select from 'react-select'
import { Badge, Banner, ImageSlot } from '../../../components'
import { useDragReorder } from '../../../hooks'
import { uploadHeroImage } from '../../../services/api'
import { arrayMove } from '../../../utils/arrayMove'
import formStyles from '../../../styles/forms.module.scss'
import selectStyles from '../../../styles/react-select.module.scss'
import styles from './HeroBannerEditor.module.scss'

// Stored metadata shape — mirrors what ACE's hydrateHeroBanner parses
// (apps/ace/internal/service/layout_db.go): slides carry `id` + plain image-URL
// strings (NOT the tc_image_link upload flow banner_image uses), a required
// `link`, and optional alt_text. `image_url` is the desktop image
// and the required default; image_url_tablet/mobile/tv are optional per-device
// variants ACE serves by device with desktop fallback (same behavior as
// banner_image's four device slots, but stored in metadata since a hero has an
// arbitrary slide list). size defaults to LARGE when absent. content_type/
// content_id link a slide to a live match; they have no authoring UI yet
// (static promos only) but are preserved when present. There is no authored
// badge: ACE derives HeroSlide.badge from the linked content's live state and
// ignores any badge in the config, so the CMS doesn't offer one.
export interface HeroSlideMetadata {
  id: string
  image_url: string
  image_url_tablet?: string
  image_url_mobile?: string
  image_url_tv?: string
  /** Image description for screen readers. Sent to the upload endpoint as the
   * tc_image alt text; persisted here so it survives reloads and edits, and
   * served to clients as HeroSlide.altText. */
  alt_text?: string
  link: string
  content_type?: string
  content_id?: string
}

// The four device image slots per slide. `image_url` (desktop) is the required
// default; the rest fall back to it at render time. Keep the type_code-free
// keys in sync with ACE's heroSlideImageURL.
const HERO_IMAGE_SLOTS: { key: keyof HeroSlideMetadata; label: string; hint: string }[] = [
  { key: 'image_url', label: 'Desktop', hint: 'Default' },
  { key: 'image_url_tablet', label: 'Tablet', hint: 'Falls back to desktop' },
  { key: 'image_url_mobile', label: 'Mobile', hint: 'Falls back to desktop' },
  { key: 'image_url_tv', label: 'TV', hint: 'Falls back to desktop' },
]

export interface HeroBannerMetadata {
  size?: 'LARGE' | 'STANDARD'
  rotation_interval_ms?: number
  slides?: HeroSlideMetadata[]
}

interface HeroBannerEditorProps {
  metadata: HeroBannerMetadata
  onChange: (metadata: HeroBannerMetadata) => void
  /** Present once the widget row exists (auto-created on name blur); required to upload slide images. */
  widgetId?: string
}

interface Opt {
  value: string
  label: string
}

const SIZE_OPTIONS: Opt[] = [
  { value: 'LARGE', label: 'Large' },
  { value: 'STANDARD', label: 'Standard' },
]

const HeroBannerEditor: React.FC<HeroBannerEditorProps> = ({ metadata, onChange, widgetId }) => {
  const slides = metadata.slides ?? []

  // Latest metadata for async callbacks (image upload) that would otherwise
  // close over a stale render's slides/index and write to the wrong slide.
  const metadataRef = useRef(metadata)
  metadataRef.current = metadata

  // Which slides are expanded for editing. Collapsed rows are compact and
  // draggable; a newly added slide opens expanded so it can be filled in.
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const update = useCallback(
    (patch: Partial<HeroBannerMetadata>) => onChange({ ...metadataRef.current, ...patch }),
    [onChange],
  )

  const commitSlides = useCallback(
    (next: HeroSlideMetadata[]) => update({ slides: next }),
    [update],
  )

  // Patch a slide by id off the latest metadata. Use this from async callbacks
  // (upload) so a reorder/edit in flight can't misroute the result.
  const patchSlideById = useCallback(
    (id: string, patch: Partial<HeroSlideMetadata>) => {
      const cur = metadataRef.current.slides ?? []
      commitSlides(cur.map(s => (s.id === id ? { ...s, ...patch } : s)))
    },
    [commitSlides],
  )

  const { dragIndex, handleProps, rowProps } = useDragReorder(
    useCallback(
      (from: number, to: number) => commitSlides(arrayMove(metadataRef.current.slides ?? [], from, to)),
      [commitSlides],
    ),
    { dragImageFromParent: true },
  )

  const toggleExpanded = (id: string) =>
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const addSlide = () => {
    const id = crypto.randomUUID()
    commitSlides([...slides, { id, image_url: '', link: '' }])
    setExpanded(prev => new Set(prev).add(id))
  }

  const removeSlide = (id: string) => {
    commitSlides((metadataRef.current.slides ?? []).filter(s => s.id !== id))
    setExpanded(prev => {
      if (!prev.has(id)) return prev
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }

  const sizeValue = SIZE_OPTIONS.find(o => o.value === (metadata.size ?? 'LARGE')) ?? SIZE_OPTIONS[0]

  return (
    <>
      <div className={formStyles.formGrid}>
        <div className={formStyles.fieldGroup}>
          <label>Size</label>
          <Select
            value={sizeValue}
            onChange={(opt) => opt && update({ size: opt.value as HeroBannerMetadata['size'] })}
            options={SIZE_OPTIONS}
            className={selectStyles.reactSelect}
            classNamePrefix="react-select"
          />
        </div>

        <div className={formStyles.fieldGroup}>
          <label title="How long each slide shows before rotating to the next.">Rotation interval (ms)</label>
          <input
            type="number"
            min={0}
            placeholder="e.g. 6000 — leave blank to not auto-rotate"
            value={metadata.rotation_interval_ms ?? ''}
            onChange={(e) =>
              update({ rotation_interval_ms: e.target.value ? parseInt(e.target.value, 10) : undefined })
            }
          />
        </div>
      </div>

      <div className={styles.slidesHeader}>
        <span>Slides</span>
        <Badge variant="secondary" size="small">{slides.length}</Badge>
      </div>

      {!widgetId && (
        <Banner variant="info" message="Name the widget above to unlock slide image uploads." />
      )}

      {slides.length === 0 && (
        <p className={formStyles.hint}>No slides yet — a hero banner needs at least one.</p>
      )}

      {slides.map((slide, i) => {
        const isOpen = expanded.has(slide.id)
        // Mirrors the API's hero validation: a slide needs an image and a link.
        const missing = !slide.image_url ? 'image' : !slide.link ? 'link' : null
        return (
          <div
            key={slide.id}
            className={`${styles.slide} ${dragIndex === i ? styles.dragging : ''}`}
            {...rowProps(i)}
          >
            <div className={styles.slideRow}>
              <span
                className={styles.handle}
                title="Drag to reorder"
                aria-label="Drag to reorder"
                {...handleProps(i)}
              >
                ⠿
              </span>

              {slide.image_url ? (
                <img src={slide.image_url} alt="" className={styles.thumb} />
              ) : (
                <span className={styles.thumbEmpty} />
              )}

              <button type="button" className={styles.summary} onClick={() => toggleExpanded(slide.id)}>
                <span className={styles.summaryTitle}>{`Slide ${i + 1}`}</span>
                {slide.link && <span className={styles.summaryLink}>{slide.link}</span>}
              </button>

              {missing && (
                <Badge variant="warning" size="small">Needs {missing}</Badge>
              )}

              <button
                type="button"
                className={styles.iconBtn}
                onClick={() => toggleExpanded(slide.id)}
                title={isOpen ? 'Collapse' : 'Edit'}
                aria-expanded={isOpen}
              >
                {isOpen ? '▲' : '▼'}
              </button>
              <button
                type="button"
                className={`${styles.iconBtn} ${styles.remove}`}
                onClick={() => removeSlide(slide.id)}
                title="Remove slide"
              >
                ✕
              </button>
            </div>

            {isOpen && (
              <div className={styles.slideBody}>
                <div className={styles.imageSlots}>
                  {HERO_IMAGE_SLOTS.map((sl) => {
                    const url = (slide[sl.key] as string | undefined) || ''
                    return (
                      <ImageSlot
                        key={sl.key}
                        label={sl.label}
                        hint={sl.hint}
                        aspectRatio="16 / 9"
                        disabled={!widgetId}
                        image={url ? { url } : null}
                        onUpload={async (file) => {
                          const { cdn_url } = await uploadHeroImage(widgetId!, file, slide.alt_text)
                          patchSlideById(slide.id, { [sl.key]: cdn_url } as Partial<HeroSlideMetadata>)
                        }}
                        onRemove={url ? () => patchSlideById(slide.id, { [sl.key]: '' } as Partial<HeroSlideMetadata>) : undefined}
                      />
                    )
                  })}
                </div>

                <div className={formStyles.fieldGroup}>
                  <label>Alt text <Badge variant="secondary" size="small">Public</Badge></label>
                  <input
                    type="text"
                    value={slide.alt_text ?? ''}
                    onChange={(e) => patchSlideById(slide.id, { alt_text: e.target.value || undefined })}
                    placeholder="Describe the image for screen readers"
                  />
                </div>

                <div className={formStyles.fieldGroup}>
                  <label>Link <Badge variant="secondary" size="small">Public</Badge></label>
                  <input
                    type="text"
                    value={slide.link}
                    onChange={(e) => patchSlideById(slide.id, { link: e.target.value })}
                    placeholder="/tournaments/roland-garros"
                  />
                </div>
              </div>
            )}
          </div>
        )
      })}

      <button type="button" onClick={addSlide} className={styles.addSlide}>
        + Add slide
      </button>
    </>
  )
}

export default HeroBannerEditor
